/**
 * ArchivesTile — Bloc Doré "Archives".
 *
 * Réceptionne tous les éléments marqués "Terminer" dans les blocs précédents.
 * Suppression définitive automatique J+15.
 *
 * Permet de :
 *  - Voir la liste des éléments archivés (avec compte à rebours)
 *  - Restaurer (replace dans son module d'origine)
 *  - Supprimer définitivement (avant J+15)
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, Archive, RotateCcw, Trash2, Clock, AlertCircle } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "@/api";


const ENTITY_LABEL = {
  lease: "Bail",
  rl31: "RL-31",
  rent_increase: "Renouvellement",
  credit_check: "Enquête crédit",
};

export default function ArchivesTile({ masterId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!masterId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/immeuble/${masterId}/archives`, { headers: authH() });
      setItems(r.data?.items || []);
    } catch {
      toast.error("Impossible de charger les archives");
    } finally {
      setLoading(false);
    }
  }, [masterId]);

  useEffect(() => { load(); }, [load]);

  const restore = async (it) => {
    if (!window.confirm(`Restaurer cet élément ?\n\nIl reviendra dans son module d'origine.`)) return;
    setBusy(it.id);
    try {
      await axios.post(`${API}/admin/immeuble/archives/${it.id}/restore`, {}, { headers: authH() });
      toast.success("Élément restauré");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(null); }
  };

  const deleteNow = async (it) => {
    if (!window.confirm(`Supprimer DÉFINITIVEMENT cet élément ?\n\nCette action est irréversible.`)) return;
    setBusy(it.id);
    try {
      await axios.delete(`${API}/admin/immeuble/archives/${it.id}`, { headers: authH() });
      toast.success("Élément supprimé définitivement");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(null); }
  };

  const daysLeft = (expiresAt) => {
    if (!expiresAt) return null;
    return Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86400000);
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="bg-white border-2 border-yellow-300 rounded-xl p-5" data-testid="archives-tile">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-yellow-100 text-yellow-700 flex items-center justify-center">
          <Archive className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-base font-bold text-slate-900">Archives</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Zone tampon : éléments « terminés » sont supprimés automatiquement après 15 jours.
            Vous pouvez les restaurer ou les supprimer manuellement avant.
          </p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 text-sm text-slate-400">
          <Archive className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Aucun élément archivé pour ce dossier.
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((it) => {
            const d = daysLeft(it.expires_at);
            const expired = d !== null && d <= 0;
            return (
              <li key={it.id} className="border rounded-lg p-3 bg-yellow-50/40 border-yellow-200" data-testid={`archive-row-${it.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-1.5 py-0.5 rounded bg-yellow-200 text-yellow-800 text-[10px] font-semibold uppercase">
                        {ENTITY_LABEL[it.entity_type] || it.entity_type}
                      </span>
                      <span className="font-semibold text-slate-800 text-sm truncate">{it.label}</span>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1.5">
                      <Clock className="w-3 h-3" />
                      Archivé le {(it.archived_at || "").slice(0, 10)}
                      {d !== null && !expired && (
                        <span className={d <= 3 ? "text-rose-600 font-semibold" : "text-slate-500"}>
                          · suppression dans {d} jour(s)
                        </span>
                      )}
                      {expired && (
                        <span className="text-rose-600 font-semibold inline-flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> En attente de purge
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => restore(it)}
                      disabled={busy === it.id}
                      className="px-2 py-1 rounded bg-white border border-emerald-300 hover:bg-emerald-50 text-emerald-700 text-[10px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
                      data-testid={`archive-restore-${it.id}`}
                    >
                      <RotateCcw className="w-3 h-3" /> Restaurer
                    </button>
                    <button
                      onClick={() => deleteNow(it)}
                      disabled={busy === it.id}
                      className="px-2 py-1 rounded bg-rose-600 hover:bg-rose-700 text-white text-[10px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
                      data-testid={`archive-delete-${it.id}`}
                    >
                      <Trash2 className="w-3 h-3" /> Supprimer
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
